import {
  View,
  Text,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  ScrollView,
  Image,
} from "react-native";
import React, { useState, useEffect, useRef } from "react";
import { observer, Observer } from "mobx-react";
import GlobalStyles from "../../../Resources/Styles";
import Colors from "../../../Resources/Color";
import Fonts from "../../../Resources/Fonts";
import { Octicons } from "@expo/vector-icons";
import { useScreenDimensions, useDevice } from "../../_layout";
import { FontAwesome } from "@expo/vector-icons";
import { SyncStatus } from "../../../Resources/Constants";
import { useTranslation } from "react-i18next";

import CustomAlert from "../../Components/CustomAlert";
import ClientFeePlanView from "./ClientFeePlanView";
import ClientFeeHistory from "./ClientFeeHistory";
import LogoImage from "../../Components/LogoImage";
import { getHeaderLogoMaxWidth } from "../../Components/logoImageHeaderSizing";
import SyncStatusIndicator from "../../Components/SyncStatusIndicator";
import SearchBar from "../../Components/SearchBar";
import TutorialLinkSection from "../../Components/TutorialLinkSection";

const CoachBilling = ({ coach }) => {
  const { t, i18n } = useTranslation();
  const { screenSize } = useScreenDimensions();
  const headerLogoMaxWidth = getHeaderLogoMaxWidth(screenSize);
  const { isMobile } = useDevice();

  const [showingRenewPlan, setShowingRenewPlan] = useState(false);
  const [showingClientFeeHistory, setShowingClientFeeHistory] = useState(false);

  useEffect(() => {
    coach.setProperty("searchTerm", "");
    coach.setProperty("sortTerm", "endDate");
    return () => coach.setProperty("searchTerm", "");
  }, []);

  const ClientTableHeader = observer(() => {
    return (
      <View
        style={{
          marginTop: screenSize === "small" ? 8 : 12,
          backgroundColor: Colors.lightGrayColor,
          height: 54,
          borderTopLeftRadius: 14,
          borderTopRightRadius: 14,
          borderWidth: 0.1,
          borderColor: Colors.borderColor,
          alignItems: "center",
        }}
      >
        <View
          style={{
            ...GlobalStyles.HStack,
            width: "100%",
            flex: 1,
            height: "100%",
            justifyContent: "space-between",
            paddingHorizontal: 10,
          }}
        >
          {[
            { key: "client", label: t("clientHeader-string"), width: "14%" },
            {
              key: "startDate",
              label: t("beginningOfQuotaHeader-string"),
              width: "10%",
            },
            { key: "fee", label: t("feeHeader-string"), width: "8%" },
            {
              key: "feeType",
              label: t("quotaTypeHeader-string"),
              width: "10%",
            },
            {
              key: "endDate",
              label: t("endOfQuotaHeader-string"),
              width: "10%",
            },
            { key: "leftToPay", label: t("toPayHeader-string"), width: "15%" },
          ].map(({ key, label, width }) => (
            <TouchableOpacity
              key={key}
              onPress={() => coach.setProperty("sortTerm", key)}
              style={{
                width,
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FontAwesome
                name="sort"
                size={12}
                color="rgb(108, 115, 127)"
                style={{ marginRight: 5, opacity: 0 }}
              />
              <Text
                style={{
                  textAlign: "center",
                  fontSize: 14,
                  fontWeight: "600",
                  color: "rgb(108, 115, 127)",
                }}
              >
                {label}
              </Text>
              <FontAwesome
                name="sort"
                size={12}
                color="rgb(108, 115, 127)"
                style={{
                  marginLeft: 5,
                  opacity: coach.sortTerm === key ? 1 : 0,
                }}
              />
            </TouchableOpacity>
          ))}

          <View
            style={{
              width: "23%",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              marginRight: 20,
            }}
          >
            <Text
              style={{
                textAlign: "center",
                fontSize: 14,
                fontWeight: "600",
                color: "rgb(108, 115, 127)",
              }}
            >
              {t("actionsHeader-string")}
            </Text>
          </View>
        </View>
      </View>
    );
  });

  const ClientRow = ({ client, index }) => {
    const FeeText = () => (
      <View
        style={{ width: "100%", flexDirection: "row", alignItems: "center" }}
      >
        <Text
          numberOfLines={1}
          style={{
            width: "100%",
            color: "black",
            textAlign: "center",
            fontSize: 12,
            padding: 2,
          }}
        >
          {client.lastFee
            ? t("feeWithCurrency-string", { fee: client.lastFee.fee })
            : "-"}
        </Text>
      </View>
    );

    const DateInput = observer(() => (
      <View
        style={{
          flexDirection: "row",
          width: "100%",
          justifyContent: "center",
        }}
      >
        <Text
          style={{
            width: "100%",
            color: "black",
            textAlign: "center",
            fontSize: 12,
            padding: 2,
          }}
        >
          {client.lastFee
            ? `${client.lastFee.fee_start_date_day} / ${client.lastFee.fee_start_date_month} / ${client.lastFee.fee_start_date_year}`
            : "-"}
        </Text>
      </View>
    ));

    const EndDate = observer(() => (
      <Text
        style={{
          width: "100%",
          color: client.owedQuotas > 0 ? "red" : Colors.grayColor,
          textAlign: "center",
          fontSize: 12,
          padding: 2,
        }}
      >
        {client.lastFeeEndDate}
      </Text>
    ));

    const ActionButton = observer(({ bool }) => {
      const text = bool ? "renewPlan-string" : "viewHistory-string";
      const action = () => {
        if (bool && !coach.isOffline) {
          setShowingRenewPlan(true);
          client.setProperty("selectedToRenew", true);
        } else if (client.lastFee) {
          client.setProperty("selectedToRenew", true);
          setShowingClientFeeHistory(true);
        }
      };
      const [isHovered, setIsHovered] = useState(false);

      return (
        <TouchableOpacity
          onPress={action}
          onMouseEnter={() => !isMobile && setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <View
            style={{
              backgroundColor: bool ? Colors.bubbleColor : "transparent",
              borderRadius: 12,
              justifyContent: "center",
              borderWidth: 0.5,
              borderColor: Colors.bubbleColor,
              // height: 30,
              paddingVertical: 6,
              paddingHorizontal: 8,

              alignItems: "center",
              flexDirection: "row",
              opacity: bool
                ? !coach.isOffline
                  ? isHovered
                    ? 0.7
                    : 1
                  : 0.5
                : client.lastFee
                ? 1
                : 0.5,
            }}
          >
            {bool && (
              <Octicons
                name="history"
                size={12}
                color={Colors.whiteColor}
                style={GlobalStyles.rowIcon}
              />
            )}
            <Text
              style={{
                textAlign: "center",
                color: bool ? Colors.whiteColor : Colors.bubbleColor,
                fontWeight: isHovered ? "600" : "normal",
                fontSize: 12,
              }}
            >
              {t(text)}
            </Text>
          </View>
        </TouchableOpacity>
      );
    });

    const Actions = () => (
      <View
        style={{
          width: "23%",
          flexDirection: "row",
          justifyContent: "space-around",
          alignItems: "center",
          marginRight: 20,
        }}
      >
        <ActionButton bool={true} />
        <ActionButton bool={false} />
      </View>
    );

    const OwedQuotas = () => (
      <Observer>
        {() => (
          <View style={{ width: "15%" }}>
            <Text
              style={{
                textAlign: "center",
                color: Colors.blackColor,
                fontSize: 12,
              }}
            >
              {client.owedAmount(t)}
            </Text>
          </View>
        )}
      </Observer>
    );

    const FeeType = observer(() => (
      <View style={{ width: "10%", alignItems: "center" }}>
        <Text
          style={{
            textAlign: "center",
            color: Colors.blackColor,
            fontSize: 12,
          }}
        >
          {client.lastFee
            ? t(
                {
                  Mensual: "monthly-string",
                  Trimestral: "quarterly-string",
                  Semestral: "semestral-string",
                  Anual: "yearly-string",
                }[client.lastFee.fee_type] || "-"
              )
            : "-"}
        </Text>
      </View>
    ));

    return (
      <View
        style={{
          backgroundColor: "white",
          height: 50,
          borderBottomRightRadius:
            index === coach.getFilteredBillingClients().length - 1 ? 14 : 0,
          borderBottomLeftRadius:
            index === coach.getFilteredBillingClients().length - 1 ? 14 : 0,
          borderTopWidth: index === 0 ? 0 : 0.1,
          borderRightWidth: 0.1,
          borderLeftWidth: 0.1,
          borderBottomWidth:
            index === coach.getFilteredClients().length - 1 ? 0.1 : 0,
          borderColor: Colors.borderColor,
          alignItems: "center",
        }}
      >
        <View
          style={{
            ...GlobalStyles.HStack,
            width: "100%",
            paddingHorizontal: 10,
            flex: 1,
            justifyContent: "space-between",
          }}
        >
          <View style={{ width: "14%" }}>
            <Text
              style={{
                textAlign: "center",
                color: Colors.blackColor,
                // fontWeight: "400",
                fontSize: 12,
              }}
              numberOfLines={2}
            >
              {client.nameForCoach} {client.surnameForCoach}
            </Text>
          </View>

          <View style={{ width: "10%" }}>
            <DateInput />
          </View>

          <View style={{ width: "8%" }}>
            <FeeText />
          </View>

          <FeeType />

          <View style={{ width: "10%" }}>
            <EndDate />
          </View>

          <OwedQuotas />

          <Actions />
        </View>
      </View>
    );
  };

  const TotalRows = () => {
    const TotalRow = observer(({ total }) => {
      return (
        <View
          style={{
            backgroundColor: "white",
            height: 50,
            borderRadius: 14,
            borderWidth: 0.1,
            borderColor: Colors.borderColor,
            alignItems: "center",
            marginLeft: 10,
          }}
        >
          <View
            style={[
              GlobalStyles.HStack,
              {
                width: "100%",
                paddingHorizontal: 10,
                flex: 1,
                justifyContent: "space-between",
                alignItems: "center",
              },
            ]}
          >
            <Text
              style={{
                textAlign: "center",
                fontSize: 14,
                fontWeight: "500",
                // color: Colors.blackColor,
                color: "rgb(108, 115, 127)",
                marginRight: 5,
              }}
            >
              {total === "Revenue"
                ? "INGRESOS TOTALES CADA MES:"
                : t("totalToCollect-string")}
            </Text>
            <Text
              style={{
                textAlign: "center",
                fontSize: 14,
                fontWeight: "bold",
                color:
                  total === "Revenue"
                    ? Colors.blackColor
                    : coach.getTotalOwed > 0
                    ? "red"
                    : coach.getTotalOwed < 0
                    ? "green"
                    : Colors.blackColor,
                // color: "rgb(108, 115, 127)",
              }}
            >
              {total === "Revenue"
                ? coach.getTotalRevenuePerMonth
                : coach.getTotalOwed(t, i18n.language)}
              {/* {t("currency-string")} */}
            </Text>
          </View>
        </View>
      );
    });

    return (
      <View
        style={{
          flexDirection: "row",
          width: "100%",
          justifyContent: "flex-end",
          marginTop: screenSize === "small" ? 8 : 12,
        }}
      >
        <TotalRow total={"Owed"} />
      </View>
    );
  };

  return (
    <View style={[styles.container, { marginBottom: 20 }]}>
      <View>
        <View
          style={{
            marginBottom: 20,
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <View style={{ flexDirection: "row", alignItems: "center" }}>
            <Text
              style={[
                Fonts.bold24,
                { marginRight: screenSize === "small" ? 8 : 12, fontWeight: "600" },
              ]}
            >
              {t("billing-string")}
            </Text>
            {coach.syncStatus !== SyncStatus.SYNCED && (
              <SyncStatusIndicator
                syncStatusManager={coach.syncStatusManager}
                onPress={() => coach.startOrResetBatchTimer()}
              />
            )}
          </View>

          <LogoImage coach={coach} maxWidth={headerLogoMaxWidth} />
        </View>

        <View style={[GlobalStyles.HStack, {}]}>
          <Observer>
            {() => (
              <SearchBar
                text={coach.searchTerm}
                onChangeText={(text) => coach.setSearchTerm(text)}
                dark={false}
                placeholder={"searchClient-string"}
                autoFocusOnDesktop={true}
              />
            )}
          </Observer>
        </View>
        {coach.showBillingInfoView && (
          <TutorialLinkSection
            text="tutorialBilling-string"
            urlPath="/billing"
            marginTop={24}
            marginBottom={4}
          />
        )}
        <ScrollView
          horizontal={true}
          style={{
            width: "100%",
            flexDirection: "row",
          }}
          contentContainerStyle={{ flex: 1 }}
          showsHorizontalScrollIndicator={false}
        >
          <View
            style={{
              minWidth: 800,
              flex: 1,
              width: "100%",
              // maxWidth: "100%",
              // borderWidth: 2,
            }}
          >
            <ClientTableHeader />
            <Observer>
              {() =>
                coach.getFilteredBillingClients().map((client, index) => (
                  <View key={client.id}>
                    <ClientRow client={client} key={client.id} index={index} />
                  </View>
                ))
              }
            </Observer>
          </View>
        </ScrollView>
        <TotalRows />
        {showingRenewPlan && coach.clientToRenew && (
          <ClientFeePlanView
            isVisible={showingRenewPlan}
            onClose={() => setShowingRenewPlan(false)}
            client={coach.clientToRenew}
          />
        )}
        {showingClientFeeHistory && coach.clientToRenew && (
          <ClientFeeHistory
            isVisible={showingClientFeeHistory}
            onClose={() => setShowingClientFeeHistory(false)}
            client={coach.clientToRenew}
          />
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.backgroundColor,
  },
});

export default CoachBilling;
